import React, { useCallback, useContext } from 'react'
import {
  Stack,
  Typography,
  ListItem as MuiListItem,
  Divider,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { WithAppbarAndSearchField } from './with-appbar-and-search-field'
import { SearchableList } from '$common/searchable-list'
import {
  MultiSelectSearchableListPageOuterProps,
  MultiSelectSearchableListPageInnerProps,
} from './multi-select-searchable-list-page.types'
import { useMultiSelectList } from './react-hooks'
import { t } from '$i18n'

const CHIPS_FIELD_HEIGHT = 50

const ChipsContainer = styled(Stack)`
  padding: 9px 0;
  background-color: white;

  overflow: auto hidden;
`

const StyledDivider = styled(Divider)`
  border-color: rgba(0, 0, 0, 0.1);
`

const SelectionContext = React.createContext({})

// TODO: Remove "ts-ignore"s
export function withMultiSelectSearchableListPage<
  Item,
  FetchParams,
  SearchParams
>({
  appBarTitle,
  SkeletonItem,
  EmptyStateComponent,
  itemSize = 57,
  fetchHook,
  searchHook,
  ChipComponent,
  selectionPrimaryKey,
  skippable = false,
  selectionLimit = 30,
  noItemSelectedText = t('multiSelectUserList.noUserSelected'),
  overscanCount = 3,
  onBack,
  onSkip,
  onConfirm,
  subtractHeight,
}: MultiSelectSearchableListPageOuterProps<Item, FetchParams, SearchParams>) {
  return function MultiSelectSearchableListPage({
    fetchHookParams,
    getSearchHookParams,
    ListItem,
  }: MultiSelectSearchableListPageInnerProps<Item, FetchParams, SearchParams>) {
    const { items, loading } = fetchHook(fetchHookParams)
    const {
      selectionMap,
      setSelection,
      deleteSelection,
      selectedItems,
      hasSelection,
    } = useMultiSelectList<Item>({ items, selectionPrimaryKey })

    const renderItem = useCallback(
      ({ itemData, style }: { itemData: any; index: number; style: any }) => {
        const selection = useContext(SelectionContext)
        const selectionCount = Object.values(selection).filter(Boolean).length
        const pkValue = itemData[selectionPrimaryKey]

        return (
          <div key={pkValue} style={style}>
            <ListItem
              {...itemData}
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              selected={!!selection[pkValue]}
              onSelectedChange={(selected: boolean) => {
                if (selectionCount < selectionLimit || !selected)
                  setSelection(pkValue, selected)
              }}
            />
          </div>
        )
      },
      [],
    )

    return (
      <WithAppbarAndSearchField
        selectionLimit={selectionLimit}
        appBarTitle={appBarTitle}
        selectionCount={selectedItems.length}
        skippable={skippable}
        loading={loading}
        onBack={onBack}
        onSkip={onSkip}
        onConfirm={() => onConfirm(selectedItems)}
        subtractHeight={subtractHeight}
      >
        {(listHeight: number, searchQuery: string) => (
          <>
            {hasSelection ? (
              <>
                <ChipsContainer direction="row">
                  {selectedItems.map(item => {
                    const pkValue = item[selectionPrimaryKey]
                    return (
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      // @ts-ignore
                      <ChipComponent
                        key={pkValue}
                        {...item}
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        onDelete={() => deleteSelection(pkValue)}
                      />
                    )
                  })}
                </ChipsContainer>
                <StyledDivider />
              </>
            ) : (
              <MuiListItem sx={{ bgcolor: 'white', height: '50px' }}>
                <Typography variant="caption1" color="primary">
                  {noItemSelectedText}
                </Typography>
              </MuiListItem>
            )}

            <SelectionContext.Provider value={selectionMap}>
              <SearchableList
                items={items}
                height={listHeight - CHIPS_FIELD_HEIGHT}
                ListItem={ListItem}
                SkeletonItem={SkeletonItem}
                EmptyStateComponent={EmptyStateComponent}
                itemSize={itemSize}
                searchQuery={searchQuery}
                searchHook={searchHook}
                searchHookParams={getSearchHookParams(searchQuery, items)}
                overscanCount={overscanCount}
                loading={loading}
                renderItem={renderItem}
              />
            </SelectionContext.Provider>
          </>
        )}
      </WithAppbarAndSearchField>
    )
  }
}
